import React, { useState, useEffect, useCallback } from 'react';
import {Link} from "react-router-dom";
import {
  Button,
  IconButton,
  Typography,
  Chip,
  Box,
  Divider,
  Grid,
  Paper,
  Backdrop
} from '@mui/material';
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaTimes,
  FaLock,
  FaExclamationTriangle,
  FaArrowDown,
  FaTags,
  FaCalendarAlt,
  FaStickyNote,
  FaPlus
} from 'react-icons/fa';
import NoteService from '../../services/noteService';

const NotesTreatments = ({ patientId, onCountUpdate, componentName }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    title: '',
    details: '',
    isPrivate: false,
    priority: 'medium',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [viewingNote, setViewingNote] = useState(null);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);

  // Constantes de prioridad
  const PRIORITY_LEVELS = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high'
  };

  // Función para cargar notas con useCallback
  const loadNotes = useCallback(async () => {
    if (!patientId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (searchTerm.trim().length >= 2) {
        response = await NoteService.searchNotes(patientId, {
          q: searchTerm,
          page: currentPage
        });
      } else {
        response = await NoteService.getNotesByPatient(patientId, { 
          page: currentPage 
        });
      }
      
      const newNotes = response.data.notes || response.notes || [];
      setNotes(newNotes);
      setPagination(response.data.pagination || response.pagination || {});
      
      // IMPORTANTE: Solo llamar onCountUpdate cuando los datos cambien
      if (newNotes.length !== notes.length) {
        onCountUpdate?.(componentName, newNotes.length);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading notes:', err);
    } finally {
      setLoading(false);
    }
  }, [patientId, searchTerm, currentPage, notes.length, onCountUpdate, componentName]);

  // Efecto para cargar notas solo cuando sea necesario
  useEffect(() => {
    if (patientId) {
      loadNotes();
    }
  }, [patientId, loadNotes]);

  // Efecto separado para actualizar contador solo cuando notes cambie
  useEffect(() => {
    if (onCountUpdate && componentName) {
      onCountUpdate(componentName, notes.length);
    }
  }, [notes.length, onCountUpdate, componentName]);

  // Manejar cambios en el formulario
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Agregar tag
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  // Remover tag
  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones del frontend
    if (!formData.title.trim() && !formData.details.trim()) {
      setError('Debes proporcionar al menos un título o detalles');
      return;
    }

    if (!patientId) {
      setError('ID de paciente requerido');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const dataToSend = {
        patientId: patientId,
        title: formData.title.trim(),
        details: formData.details.trim(),
        isPrivate: formData.isPrivate,
        priority: formData.priority,
        tags: formData.tags
      };

      let response;
      if (editingNote) {
        response = await NoteService.updateNote(editingNote._id, dataToSend);
      } else {
        response = await NoteService.createNote(dataToSend);
      }
      
      setShowForm(false);
      setEditingNote(null);
      resetForm();
      await loadNotes(); // Esto actualizará automáticamente el contador
      
    } catch (err) {
      console.error('Error completo:', err);
      
      if (err.response?.data?.errors) {
        const validationErrors = err.response.data.errors.map(e => e.msg).join(', ');
        setError(`Errores de validación: ${validationErrors}`);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(err.message || 'Error desconocido al guardar la nota');
      }
    } finally {
      setLoading(false);
    }
  };

  // Resetear formulario
  const resetForm = () => {
    setFormData({
      title: '',
      details: '',
      isPrivate: false,
      priority: PRIORITY_LEVELS.MEDIUM,
      tags: []
    });
    setTagInput('');
  };

  // Ver nota completa
  const handleViewNote = (note) => {
    setViewingNote(note);
    setShowViewOffcanvas(true);
  };

  // Cerrar offcanvas de vista
  const handleCloseView = () => {
    setShowViewOffcanvas(false);
    setViewingNote(null);
  };

  // Editar nota
  const handleEdit = (note) => {
    setEditingNote(note);
    setFormData({
      title: note.title || '',
      details: note.details || '',
      isPrivate: note.isPrivate || false,
      priority: note.priority || PRIORITY_LEVELS.MEDIUM,
      tags: note.tags || []
    });
    setShowForm(true);
  };

  // Eliminar nota
  const handleDelete = async (noteId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta nota?')) {
      try {
        setDeletingNoteId(noteId);
        await NoteService.deleteNote(noteId);
        await loadNotes(); // Esto actualizará automáticamente el contador
      } catch (err) {
        setError(err.message);
        console.error('Error deleting note:', err);
      } finally {
        setDeletingNoteId(null);
      }
    }
  };

  // Cancelar formulario
  const handleCancel = () => {
    setShowForm(false);
    setEditingNote(null);
    resetForm();
  };

  // Función para obtener el color de la prioridad
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  // Función para obtener el icono de la prioridad
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return <FaExclamationTriangle />;
      case 'low': return <FaArrowDown />;
      default: return null;
    }
  };

  if (loading && notes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </Box>
    );
  }

  return (
    <div className="p-4 bg-white h-100 rounded-10 shadow-sm">
      {/* Header */}
      <h3 className="mb-3" style={{ fontSize: '1.125rem' }}>Notes</h3>
        
      
      {/* Enhanced Search and Actions Bar */}
      <div className="filter-container mb-4">
        <div className="row">

            {/* Search Input */}
            <div className="col-md-5 col-lg-6 d-none d-md-block mb-3 mb-md-0">
              <input
                type="text"
                className="form-control"
                placeholder="Search by note"
                // value={globalFilter}
                // onChange={(e) => handleGlobalFilterChange(e.target.value)}
              />
            </div>

            <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-1 gap-sm-2 flex-wrap">
              

              {/* Add New Button */}
              <button
                className="btn btn-primary addnewbtn d-flex gap-2"
                onClick={() => setShowForm(true)}
              >
                Add Note
              </button>
            </div>

        </div>
      </div>




      
      

      {/* Content */}
      <Box>
        {/* Error Message */}
        {error && (
          <Box sx={{ mb: 3 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: 'error.light', 
                color: 'error.contrastText',
                borderRadius: 2
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {error}
              </Typography>
              <Button 
                size="small"
                onClick={() => setError(null)}
                sx={{ color: 'error.contrastText', textDecoration: 'underline', p: 0 }}
              >
                Cerrar
              </Button>
            </Paper>
          </Box>
        )}

        {/* Notes List */}
        <Box>
          {notes.length === 0 ? (
            <Box>
              <FaStickyNote size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
              <Typography variant="body2">No notes available</Typography>
            </Box>
          ) : (
            <ul className="list-unstyled mb-0">
              {notes.map((note, idx) => (
                <li
                  key={note._id}
                  className={`d-flex justify-content-between gap-3 pb-3 ${idx !== notes.length - 1 ? 'border-bottom mb-3' : ''}`}
                >
                  <div>
                    <p className="fs-sm text-gray lh-1 mb-2">
                      {new Date(note.createdAt).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                    <p className="fs-sm lh-1 mb-1 fw-bold">{note.title}</p>
                    <p className="fs-sm lh-1 mb-0">{note.details}</p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {/* Status badges */}
                    {note.isPrivate && (
                      <span className="badge bg-warning text-dark d-flex align-items-center gap-1">
                        <FaLock size={11} />
                        Private
                      </span>
                    )}
                    {note.priority && note.priority !== 'medium' && (
                      <span className={`badge bg-${note.priority === 'high' ? 'danger' : 'success'} d-flex align-items-center gap-1`}>
                        {getPriorityIcon(note.priority)}
                        {note.priority.charAt(0).toUpperCase() + note.priority.slice(1)} Priority
                      </span>
                    )}
                    {note.tags && note.tags.length > 0 && note.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="badge bg-secondary d-flex align-items-center gap-1">
                        <FaTags size={11} />
                        {tag}
                      </span>
                    ))}
                    {/* Actions Dropdown */}
                    <div className="dropdown">
                      <Link
                        href="#"
                        className="text-gray text-decoration-none"
                        role="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="d-block material-symbols-outlined">more_vert</span>
                      </Link>
                      <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                          <button
                            className="dropdown-item d-flex align-items-center gap-2"
                            type="button"
                            onClick={() => handleViewNote(note)}
                            disabled={loading}
                          >
                            <FaEye size={14} /> View
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item d-flex align-items-center gap-2"
                            type="button"
                            onClick={() => handleEdit(note)}
                            disabled={loading}
                          >
                            <FaEdit size={14} /> Edit
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item d-flex align-items-center gap-2 text-danger"
                            type="button"
                            onClick={() => handleDelete(note._id)}
                            disabled={loading || deletingNoteId === note._id}
                          >
                            {deletingNoteId === note._id ? (
                              <div className="animate-spin w-3 h-3 border border-red-600 border-t-transparent rounded-full"></div>
                            ) : (
                              <FaTrash size={14} />
                            )}
                            Delete
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Box>

        

        {/* Offcanvas para Formulario */}
        <div 
          className={`offcanvas offcanvas-end ${showForm ? 'show' : ''}`} 
          tabIndex="-1" 
          style={{ 
            visibility: showForm ? 'visible' : 'hidden',
            width: '500px',
            maxWidth: '90vw'
          }}
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="offcanvas-title d-flex align-items-center gap-2">
              <FaStickyNote />
              {editingNote ? 'Edit Note' : 'Add New Note'}
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleCancel}
            ></button>
          </div>
          
          <div className="offcanvas-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label small text-muted">Title</label>
                <input
                  type="text"
                  name="title"
                  placeholder="Enter note title"
                  value={formData.title}
                  onChange={handleFormChange}
                  className="form-control"
                />
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted">Details</label>
                <textarea
                  name="details"
                  placeholder="Enter note details"
                  value={formData.details}
                  onChange={handleFormChange}
                  rows={8}
                  className="form-control"
                  style={{ resize: 'none' }}
                />
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted">Priority</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleFormChange}
                  className="form-select"
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted">Tags</label>
                <div className="input-group mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add a tag"
                    className="form-control"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="btn btn-outline-primary"
                  >
                    Add
                  </button>
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="badge bg-primary d-flex align-items-center gap-1">
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="btn-close btn-close-white"
                        style={{ fontSize: '10px' }}
                      ></button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="isPrivate"
                    id="isPrivate"
                    checked={formData.isPrivate}
                    onChange={handleFormChange}
                    className="form-check-input"
                  />
                  <label className="form-check-label d-flex align-items-center gap-1" htmlFor="isPrivate">
                    <FaLock size={12} />
                    Mark as Private
                  </label>
                </div>
              </div>

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  {loading ? 'Saving...' : (editingNote ? 'Update Note' : 'Save Note')}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    minWidth: '100px'
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Offcanvas para Ver Nota */}
        <div 
          className={`offcanvas offcanvas-end ${showViewOffcanvas ? 'show' : ''}`} 
          tabIndex="-1" 
          style={{ 
            visibility: showViewOffcanvas ? 'visible' : 'hidden',
            width: '600px',
            maxWidth: '90vw'
          }}
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="offcanvas-title d-flex align-items-center gap-2">
              <FaStickyNote />
              Note Details
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleCloseView}
            ></button>
          </div>
          
          <div className="offcanvas-body">
            {viewingNote && (
              <div>
                {/* Título */}
                {viewingNote.title && (
                  <div className="mb-4">
                    <label className="form-label small text-muted">Title</label>
                    <div className="p-3 bg-light rounded border">
                      <h6 className="mb-0">{viewingNote.title}</h6>
                    </div>
                  </div>
                )}

                {/* Detalles */}
                {viewingNote.details && (
                  <div className="mb-4">
                    <label className="form-label small text-muted">Details</label>
                    <div className="p-3 bg-light rounded border" style={{ whiteSpace: 'pre-wrap' }}>
                      {viewingNote.details}
                    </div>
                  </div>
                )}

                <hr className="my-4" />

                {/* Metadata */}
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <label className="form-label small text-muted d-flex align-items-center gap-1">
                      <FaCalendarAlt />
                      Created
                    </label>
                    <div className="small">
                      {new Date(viewingNote.createdAt).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>

                  {viewingNote.updatedAt && viewingNote.updatedAt !== viewingNote.createdAt && (
                    <div className="col-12">
                      <label className="form-label small text-muted">Last Updated</label>
                      <div className="small">
                        {new Date(viewingNote.updatedAt).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Tags y status */}
                <div className="mb-4">
                  <label className="form-label small text-muted">Status & Tags</label>
                  <div className="d-flex flex-wrap gap-2">
                    {viewingNote.isPrivate && (
                      <span className="badge bg-warning d-flex align-items-center gap-1">
                        <FaLock size={10} />
                        Private
                      </span>
                    )}

                    {viewingNote.priority && viewingNote.priority !== 'medium' && (
                      <span className={`badge bg-${viewingNote.priority === 'high' ? 'danger' : 'success'} d-flex align-items-center gap-1`}>
                        {getPriorityIcon(viewingNote.priority)}
                        {viewingNote.priority} Priority
                      </span>
                    )}

                    {viewingNote.tags && viewingNote.tags.length > 0 && 
                      viewingNote.tags.map((tag, index) => (
                        <span key={index} className="badge bg-primary d-flex align-items-center gap-1">
                          <FaTags size={10} />
                          {tag}
                        </span>
                      ))
                    }
                  </div>
                </div>

                {/* Actions */}
                <div className="d-flex gap-2 mt-auto pt-3 border-top">
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleEdit(viewingNote);
                      handleCloseView();
                    }}
                    startIcon={<FaEdit />}
                    fullWidth
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5
                    }}
                  >
                    Edit Note
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCloseView}
                    sx={{
                      textTransform: 'none',
                      minWidth: '100px',
                      py: 1.5
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Backdrop para los offcanvas */}
        {(showForm || showViewOffcanvas) && (
          <div 
            className="offcanvas-backdrop fade show" 
            onClick={() => {
              if (showForm) handleCancel();
              if (showViewOffcanvas) handleCloseView();
            }}
          ></div>
        )}
      </Box>
    </div>
  );
};

export default NotesTreatments;